import React, { useState, useCallback } from 'react';
import classNames from 'classnames/bind';
import { uaParser, mobileAppBridge, ENCAR_FEM, ENCAR_FEM_BUY, Enlog } from '@encarpkg/shared';
import { BottomSheet, Tabs } from '@encarpkg/design';
import styles from './EstimateCommonBottomSheet.module.scss';

const cx = classNames.bind(styles);

const femHost = process.env.NEXT_PUBLIC_ENCAR_FEM;

const paymentMethodGuideTabList = [
	{ id: 0, name: '후불 결제' },
	{ id: 1, name: '선불 결제' },
];

function EstimateCommonBottomSheet({
	type,
	onClose,
	defaultTabActive = 0,
	isVehicleTransferAvailable = false,
	isFreeInterest = false,
	// 수입차 여부 — 보증서비스 안내 링크를 보증 홈 수입 탭으로 분기
	isImported = false,
	hitType = 'hs_payment',
}) {
	// 현재 선택된 탭 정보
	const [tabsInfo, setTabsInfo] = useState({
		active: defaultTabActive ?? 0,
	});

	const bottomSheetOkButtonProps = {
		size: 44,
		color: 'black_outlined',
		onClick: onClose,
	};

	const handleEncarpayIntro = useCallback((e) => {
		e.preventDefault();
		if (uaParser().isApp) {
			mobileAppBridge().goNativeWindow(`${femHost}/encar-pay`, 'get', '', '');
		} else {
			window.open(`${femHost}/encar-pay`, 'encarpayGuide', 'width=980,height=800,scrollbars=yes');
		}
	}, []);

	// 홈서비스 화면은 PC 에서도 이미 팝업창 안이라 약관 링크처럼 같은 창에서 페이지 이동으로 연다 (재신청 상태는 sessionStorage 에 persist 되어 돌아와도 유지된다).
	// 보증 홈은 ?view=mobile(buy WARRANTY_HOME_VIEW_QUERY_KEY) 로 PC 에서도 모바일 레이아웃으로 그린다
	const warrantyGuideUrl = `${ENCAR_FEM_BUY}/warranty?view=mobile${isImported ? '&type=imported' : ''}`;

	const onClickTabs = useCallback(
		(id) => {
			if (tabsInfo.active === id) return;
			setTabsInfo({ active: id });
		},
		[tabsInfo.active],
	);

	const getOnlineTransferAvailableText = () => {
		if (isVehicleTransferAvailable) {
			return '복잡한 차량 명의 이전 업무를 엔카가 대행해 드리면서 받는 금액입니다. 예상 금액보다 이전대행료가 적게 발생한 경우, 차액을 고객님의 계좌로 환급해 드립니다.';
		}
		return '복잡한 차량 명의 이전 업무를 판매자가 고객님 대신해 드리면서 받는 금액입니다.';
	};

	return (
		<>
			{/* 이전비 */}
			<BottomSheet.V2Renew
				isOpen={type === 'transferCostType'}
				useDimClickClose
				hasCloseBtn={false}
				onClose={onClose}
				title="이전비"
				buttonModuleProps={{
					okButtonProps: bottomSheetOkButtonProps,
				}}
				contentTopGap={4}
			>
				<p className={cx('desc')}>
					차량 구매 후, 차량의 명의를 고객님 명의로 변경할 때 발생하는 세금이에요. 취득세, 공채 수수료, 증/인지대, 등기비용 등이 포함돼요.
				</p>
			</BottomSheet.V2Renew>
			{/* 관리비(매도비) */}
			<BottomSheet.V2Renew
				isOpen={type === 'sellingCostType'}
				useDimClickClose
				hasCloseBtn={false}
				onClose={onClose}
				title="관리비(매도비)"
				buttonModuleProps={{
					okButtonProps: bottomSheetOkButtonProps,
				}}
				contentTopGap={4}
			>
				<p className={cx('desc')}>
					자동차 관리법 시행 규칙 제112조에 따라 중고차를 구매할 때 차량을 보관하거나 유지 보수하는 데 쓰인 관리 비용을 지불해야 해요.
				</p>
			</BottomSheet.V2Renew>
			{/* 배송비 */}
			<BottomSheet.V2Renew
				isOpen={type === 'shippingCostType'}
				useDimClickClose
				hasCloseBtn={false}
				onClose={onClose}
				title="배송비"
				buttonModuleProps={{
					okButtonProps: bottomSheetOkButtonProps,
				}}
				contentTopGap={4}
			>
				<p className={cx('desc')}>배송비는 차량 출발지와 배송지 사이의 거리에 따라 책정돼요.</p>
			</BottomSheet.V2Renew>
			{/* 이전대행료 */}
			<BottomSheet.V2Renew
				isOpen={type === 'transferAgencyFeeType'}
				useDimClickClose
				hasCloseBtn={false}
				onClose={onClose}
				title="이전대행료"
				buttonModuleProps={{
					okButtonProps: bottomSheetOkButtonProps,
				}}
				contentTopGap={4}
			>
				<p className={cx('desc')}>{getOnlineTransferAvailableText()}</p>
			</BottomSheet.V2Renew>
			{/* 차량가격에 다른비용이 추가된 이유 */}
			<BottomSheet.V2Renew
				isOpen={type === 'paymentReasonType'}
				useDimClickClose
				hasCloseBtn={false}
				onClose={onClose}
				title="차량가격에 다른비용이 추가된 이유"
				buttonModuleProps={{
					okButtonProps: bottomSheetOkButtonProps,
				}}
				contentTopGap={4}
			>
				<p className={cx('desc')}>중고차 구매 시 법적으로 내야하는 비용(이전등록비, 관리비등)과 엔카믿고 이용료가 추가 된 금액이에요.</p>
			</BottomSheet.V2Renew>
			{/* 차량가격에 다른비용이 추가된 이유 */}
			<BottomSheet.V2Renew
				isOpen={type === 'encarPayType'}
				useDimClickClose
				hasCloseBtn
				onClose={onClose}
				title="계약금에 대하여"
				buttonModuleProps={{
					okButtonProps: { ...bottomSheetOkButtonProps, children: '자세히 보기', onClick: (e) => handleEncarpayIntro(e) },
				}}
				contentTopGap={4}
			>
				<p className={cx('desc')}>
					차량의 일부 금액만 미리 계약금으로 내고 차를 타볼 수 있어요. 계약금은 개인의 지불한도에 따라 달라져요. 고객님의 계약금이 궁금하면
					엔카페이를 선택해 보세요.
					<span className={cx('layer_desc')}>계약금을 조회해도 신용도에 영향은 전혀 없어요!</span>
				</p>
			</BottomSheet.V2Renew>
			{/*  예상 계약금 */}
			<BottomSheet.V2Renew
				isOpen={type === 'postpaidAmountEstimateType'}
				useDimClickClose
				hasCloseBtn={false}
				onClose={onClose}
				title="예상 계약금에 대하여"
				buttonModuleProps={{
					okButtonProps: bottomSheetOkButtonProps,
				}}
				contentTopGap={4}
			>
				<p className={cx('desc')}>
					지불가능여부 조회 후, 확정된 계약금을 확인할 수 있어요.
					<br />
					신청 접수를 완료하면, 지불가능여부 조회(신용 조회)가 진행돼요!
					<span className={cx('layer_desc')}>조회시 신용점수에는 영향을 주지 않으니 안심하고 확인해 보세요!</span>
				</p>
			</BottomSheet.V2Renew>
			{/* 결제 방법 안내 */}
			<BottomSheet.V2Renew
				isOpen={type === 'paymentMethodGuide'}
				useDimClickClose
				hasCloseBtn={false}
				onClose={onClose}
				title="결제 방법 안내"
				buttonModuleProps={{
					okButtonProps: bottomSheetOkButtonProps,
				}}
				contentTopGap={4}
			>
				<>
					<Tabs
						shape="segment"
						size="small"
						list={paymentMethodGuideTabList.map((item) => item.name)}
						scrollActive
						active={tabsInfo.active}
						onClick={onClickTabs}
					/>
					<div className={cx('wrap_payment_method_guide')}>
						{tabsInfo.active === 0 && (
							<div className={cx('desc')}>
								차량의 가격의 일부를 계약금으로 결제하고, 나머지 금액은 차량을 직접 타보고 결제하는 금융 상품(
								<a
									href={`${ENCAR_FEM}/encar-pay`}
									target="_blank"
									rel="noopener noreferrer"
									className={cx('emph')}
								>
									엔카페이
								</a>
								)에요.
								<ul className={cx('layer_desc')}>
									<li>※ 계약금은 차량 가격과 금융 상품 내부 기준에 따라 달라질 수 있어요.</li>
									<li>※ 구매확정 시 잔금은 현금 또는 할부로 결제 가능해요.</li>
									<li>※ 후불결제 금융 상품 이용을 위한 할부 가계약을 진행해야 돼요.</li>
									<li>※ 대출 금액이 300만원 미만일 경우 금융상품(엔카페이) 이용이 제한될 수 있습니다.</li>
								</ul>
							</div>
						)}
						{tabsInfo.active === 1 && (
							<>
								<div className={cx('wrap_payment_method')}>
									<span className={cx('payment_method_tit')}>현금</span>
									<p className={cx('payment_method_desc')}>
										차량 금액과 부대비용을 현금으로 한 번에 결제하고 차량을 가장 빠르게 받아볼 수 있어요.
									</p>
									<ul className={cx('payment_method_detail')}>
										<li>※ 결제 전 이체 한도를 미리 확인해 주세요.</li>
										<li>※ 기상악화 및 탁송사 사정에 따라 배송일정은 변동될 수 있어요.</li>
									</ul>
								</div>
								{isFreeInterest && (
									<div className={cx('wrap_payment_method')}>
										<span className={cx('payment_method_tit')}>무이자 할부</span>
										<p className={cx('payment_method_desc')}>
											차량 금액은 전액 무이자 할부로 결제할 수 있어요
											<br />
											차량 부대비용만 결제하고 차량을 받아보세요.
										</p>
										<ul className={cx('payment_method_detail')}>
											<li>※ 부대비용(계약금)은 차량 가격과 금융 상품 내부 기준에 따라 달라질 수 있어요.</li>
											<li>※ 대출 금리는 개인의 신용도에 따라 달라져요.</li>
											<li>※ 할부 개월 수는 6개월 고정이며 변경이 어려워요.</li>
											<li>※ 대출 금액이 300만원 미만인 경우 금융상품(대출) 이용이 제한될 수 있어요.</li>
										</ul>
									</div>
								)}
								<div>
									<span className={cx('payment_method_tit')}>대출 + 현금</span>
									<p className={cx('payment_method_desc')}>
										엔카 매니저와 함께 대출을 알아보고
										<br />
										제휴 금융사를 통해 우대금리를 받을 수 있어요.
									</p>
									<ul className={cx('payment_method_detail')}>
										<li>※ 차량 가격을 제외한 부대비용은 현금결제가 필요해요.</li>
										<li>※ 대출 금리는 개인의 신용도에 따라 달라져요.</li>
										<li>※ 대출 금액이 300만원 미만일 경우 금융상품(대출) 이용이 제한될 수 있어요.</li>
										<li>
											※ 금융상품에 관한 구체적인 내용(금리, 한도, 대출실행 등)은 고객님이 금융회사와 체결하는 이용약관,
											상품설명서 및 관련 법령 등에 따릅니다.
										</li>
									</ul>
								</div>
							</>
						)}
					</div>
				</>
			</BottomSheet.V2Renew>
			{/* 성능보험료 */}
			<BottomSheet.V2Renew
				isOpen={type === 'liabilityInsuranceFeeType'}
				useDimClickClose
				hasCloseBtn={false}
				onClose={onClose}
				title="성능보험료 (성능ㆍ상태점검 책임보험료)"
				buttonModuleProps={{
					okButtonProps: bottomSheetOkButtonProps,
				}}
				contentTopGap={4}
			>
				<p className={cx('desc')}>
					차량의 성능 및 상태를 보증하기 위해 가입하는 보험료입니다. 차량 연식, 가격, 지역 등에 따라 금액이 달라질 수 있습니다.
				</p>
			</BottomSheet.V2Renew>
			{/* 보증서비스 */}
			<BottomSheet.V2Renew
				isOpen={type === 'warrantyServiceType'}
				useDimClickClose
				hasCloseBtn={false}
				onClose={onClose}
				title="보증서비스"
				buttonModuleProps={{
					okButtonProps: bottomSheetOkButtonProps,
				}}
				data-enlog-dt-eventnamegroup="종합보증비설명팝업"
			>
				<p className={cx('desc')}>
					차량의 엔진, 미션, 제동, 일반 부품에 문제가 발생했을 때 보증 수리를 받을 수 있어요.
					<br />
					<br />
					*엔카는 차량 거래를 중개하는 서비스예요, 보증 서비스를 구입하지 않은 경우, 해당 부품 수리는 지원되지 않아요.
					<br />
					*보증 서비스에 대한 자세한 설명은{' '}
					<a
						href={warrantyGuideUrl}
						className={cx('link')}
						onClick={() => {
							Enlog().enloggingJsonData({
								dataTalkClick: {
									eventname: '서비스안내',
									eventnamegroup: '종합보증비설명팝업',
									hit: hitType,
								},
							});
						}}
					>
						서비스 안내
					</a>
					를 참고하세요.
				</p>
			</BottomSheet.V2Renew>
		</>
	);
}

export default EstimateCommonBottomSheet;
